import { useCallback, useState } from 'react';

/** FR "List and Table Pagination": more than ten rows pages, ten at a time. */
export const ORBIT_PAGE_SIZE = 10;

/**
 * FR: "Large result sets shall remain a Generate Report use case and must not be fully rendered in
 * the conversation." Past this many rows the transcript stops paging and offers the report instead.
 */
export const ORBIT_ROW_CAP = 50;

export interface OrbitPaging {
  /** Rows the caller should slice off the top. */
  shown: number;
  total: number;
  /** The set is long enough to page at all; below this no count line and no control are drawn. */
  paginated: boolean;
  canShowMore: boolean;
  /** Rows remain but the cap has been reached: the report replaces `Show more`. */
  atCap: boolean;
  showMore: () => void;
}

/**
 * Client-side paging over rows already delivered. The FR describes `Show more` as a server round
 * trip that re-applies authorization per page; the contract returns the whole `series[]` inline and
 * has no paging endpoint, so there is no request to re-apply it on. Recorded as a divergence.
 */
export const useOrbitPaging = (total: number): OrbitPaging => {
  const [pages, setPages] = useState(1);
  const shown = Math.min(total, pages * ORBIT_PAGE_SIZE, ORBIT_ROW_CAP);
  const showMore = useCallback(() => setPages(previous => previous + 1), []);

  return {
    shown,
    total,
    paginated: total > ORBIT_PAGE_SIZE,
    canShowMore: shown < total && shown < ORBIT_ROW_CAP,
    atCap: shown < total && shown >= ORBIT_ROW_CAP,
    showMore
  };
};

export default useOrbitPaging;
